import { useState, type FormEvent } from 'react';

const SUBMIT_URL = 'https://readdy.ai/api/form/db7k0uitp9sllopf7gt0';

const inputClass =
  'w-full h-11 px-3.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors';

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    // 反垃圾：正常用户不会填写此字段
    const honeypot = String(data.get('contact_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }

    setStatus('submitting');
    setFormError('');

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === 'contact_alt') return;
      payload.append(key, String(value));
    });

    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(
          typeof serverMsg === 'string' && serverMsg && !isSpam
            ? serverMsg
            : '提交失败，请稍后重试，或直接发送邮件联系我们。'
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  if (status === 'success') {
    return (
      <div className="p-8 rounded-lg bg-background-50 border border-background-200 text-center">
        <span className="w-14 h-14 mx-auto rounded-full bg-accent-100 text-accent-600 flex items-center justify-center">
          <i className="ri-checkbox-circle-line text-3xl"></i>
        </span>
        <h3 className="mt-4 text-lg font-bold text-foreground-950">感谢你的留言！</h3>
        <p className="mt-2 text-sm text-foreground-500 leading-relaxed">
          我们已经收到你的消息，会尽快查看并回复。
          演示站点不会存储留言内容，感谢你的理解与支持。
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-edit-line w-4 h-4 flex items-center justify-center"></i>
          再写一条留言
        </button>
      </div>
    );
  }

  return (
    <form
      id="contact-message-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="p-6 md:p-8 rounded-lg bg-background-50 border border-background-200 space-y-5"
    >
      <div>
        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
          姓名 <span className="text-accent-600">*</span>
        </label>
        <input
          id="contact-name"
          name="name"
          type="text"
          required
          autoComplete="name"
          maxLength={50}
          placeholder="请输入你的称呼"
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
          邮箱 <span className="text-accent-600">*</span>
        </label>
        <input
          id="contact-email"
          name="email"
          type="email"
          required
          autoComplete="email"
          maxLength={80}
          placeholder="you@example.com"
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
          留言内容 <span className="text-accent-600">*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={5}
          maxLength={500}
          placeholder="想聊聊哪部电影？或者有什么建议想告诉我们？"
          className="w-full px-3.5 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
        ></textarea>
        <p className="mt-1 text-xs text-foreground-400">最多 500 字</p>
      </div>

      <input
        className="contact-form-extra-note"
        type="text"
        name="contact_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {status === 'error' && formError && (
        <p className="flex items-start gap-2 text-sm text-accent-700 bg-accent-50 border border-accent-200 rounded-md px-3.5 py-2.5">
          <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center mt-0.5 shrink-0"></i>
          {formError}
        </p>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
      >
        {status === 'submitting' ? (
          <>
            <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
            正在提交…
          </>
        ) : (
          <>
            <i className="ri-send-plane-line w-4 h-4 flex items-center justify-center"></i>
            提交留言
          </>
        )}
      </button>
    </form>
  );
}